import './ConflictingBetsSelection.less';

import React, { useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import clsx from 'clsx';
import { LastCmdError, PickJson } from '../types';
import i18n from '../../../common/i18n';
import RadioButton from '../../forms/RadioButton';
import { Button } from '../../button/Button';
import { DialogProps } from '../../dialogs/dialogs';
import { betslipPick$$ } from './subs';
import { clearLastCmdError } from './handlers';
import { formatNumber } from '../../../common/util';
import { useLivePick } from './hooks';
import { OddsChange, useOddsChange } from '../../turboLive/hooks/useOddsChange';
import { nuiCtx, NuiPlatform } from '../../../common/initialization';
import { SportIconNui } from '../../../icons/SportIconNui';

export interface ConflictingBetsSelectionProps extends DialogProps {
    cmdError: LastCmdError;
}

export function ConflictingBetsSelection({ cmdError, close }: ConflictingBetsSelectionProps) {
    const [shouldReplace, setShouldReplace] = useState(true);
    const { newPick } = cmdError.conflicts;
    const { enabled: newPickEnabled, odds: newPickOdds } = useLivePick(newPick);
    const platform = nuiCtx.platform as NuiPlatform;
    const forceAddPickCmd = [
        platform === 'mobile' ? 'AddPickMobile' : 'AddPick',
        {
            betId: newPick.betId,
            selectionCode: newPick.selectionCode,
            odds: newPick.odds,
            positionId: newPick.positionId,
            positionCode: newPick.positionCode,
            removeConflicts: true
        }
    ];
    const confirm = () => {
        dispatch(['betslip.cmd', forceAddPickCmd]);
    };
    return (
        <div className={'conflicting-bets'}>
            <div className="fs-18 text-extra-bold text-center px-30 pb-10 uppercase">
                {i18n('nui.betslip.conflictingBetsDialog.title')}
            </div>
            <div className={'conflicting-bets-subtitle text-center'}>
                <div>{i18n('nui.betslip.conflictingBetsDialog.subtitle')}</div>
            </div>
            <div className={'py-20'}>
                <RadioButton active={shouldReplace === true} onClick={() => setShouldReplace(true)}>
                    <Pick pick={newPick} enabled={newPickEnabled} odds={newPickOdds} />
                </RadioButton>

                <div className={'flex'}>
                    <div className={'flex-1 conflicting-bets-delimeter'} />
                    <div className={'conflicting-bets-subtitle lowercase px-10'}>
                        {i18n('nui.betslip.conflictingBetsDialog.delimeter')}
                    </div>
                    <div className={'flex-1 conflicting-bets-delimeter'} />
                </div>

                <RadioButton active={shouldReplace === false} onClick={() => setShouldReplace(false)}>
                    <div className={'flex flex-1 flex-col'}>
                        {cmdError.conflicts.conflictingPicks.map((conflictingPick, i) => (
                            <div key={i}>
                                {!!i && <div className={'conflicting-bets-delimeter'} />}
                                <ExistingPickWrapper
                                    selectionId={conflictingPick.selectionId}
                                    betslipIndex={cmdError.betslipIndex}
                                />
                            </div>
                        ))}
                    </div>
                </RadioButton>
                <div className="nui-button-row pt-20">
                    <Button
                        className="nui-button br-radius-xs grey"
                        label={i18n('nui.button.cancel')}
                        onClick={() => {
                            dispatch(clearLastCmdError(platform));
                            close();
                        }}
                        dataAtid={'conflicting-bets-modal-cancel-btn'}
                    />
                    <Button
                        className="nui-button-180 br-radius-xs ml-20"
                        primary
                        disabled={!newPickEnabled && shouldReplace}
                        label={i18n('nui.button.confirm')}
                        onClick={() => {
                            if (shouldReplace) {
                                confirm();
                            } else {
                                dispatch(clearLastCmdError(platform));
                            }
                            close();
                        }}
                        dataAtid={'conflicting-bets-modal-confirm-btn'}
                    />
                </div>
            </div>
        </div>
    );
}

function ExistingPickWrapper({ selectionId, betslipIndex }: { selectionId: string; betslipIndex: number }) {
    const { subscribe } = useReframe();
    const pick: PickJson | undefined = subscribe(betslipPick$$(selectionId, betslipIndex));
    if (!pick) {
        return null;
    }

    return <Pick pick={pick} enabled={!pick.invalid} />;
}

function Pick({ pick, enabled, odds }: { pick: PickJson; enabled?: boolean; odds?: number }) {
    const oddsChange = useOddsChange(odds || pick.odds, '');
    return (
        <div
            className={'conflicting-bets-pick flex flex-1 flex-col'}
            data-atid={`conflicting-bets-pick-${pick?.infoNumber}`}
        >
            <div className={'flex'}>
                <div className={'flex-1 fs-14'}>
                    <span className="text-extra-bold pr-5 c-black-80 mr-10">
                        <span>{pick.home}</span>
                        {pick.away && (
                            <>
                                <span className="c-black-40 px-3">vs</span>
                                <span>{pick.away}</span>
                            </>
                        )}
                    </span>
                </div>
                <div>
                    <SportIconNui sportId={pick.sportId} className={`conflicting-bets-sport-icon flex-shrink-0`} />
                </div>
            </div>
            <div className={'flex'}>
                <div className={'conflicting-bets-subtitle flex-1'}>
                    <span>{pick.market}: </span>
                    <span className={'text-extra-bold'}>{pick.header}</span>
                </div>
                {!pick.isLive && <div className={'fs-14 text-extra-bold'}>{formatNumber(pick.odds)}</div>}
                {pick.isLive && enabled && (
                    <div
                        className={clsx('fs-14 text-extra-bold', {
                            'c-red': oddsChange === OddsChange.Decreased,
                            'c-green': oddsChange === OddsChange.Increased
                        })}
                    >
                        {formatNumber(odds || pick.odds)}
                    </div>
                )}
                {pick.isLive && !enabled && (
                    <div className={'conflicting-bets-subtitle lowercase'}>
                        {i18n('nui.betslip.conflictingBetsDialog.betDisabled')}
                    </div>
                )}
            </div>
        </div>
    );
}
